import axios from "axios";

import { createUrl } from "./createUrl";

// axios's get/delete are (url, config) — a third argument is ignored, so the
// config (and with it withCredentials and the abort signal) must be second.
// delete's body rides in config.data; get intentionally never sends one,
// because callers that stash data on the constructor have never had it sent.
function sendRequest(verb, url, data, config) {
  if (verb === "get") return axios.get(url, config);
  if (verb === "delete") return axios.delete(url, { ...config, data });
  return axios[verb](url, data, config);
}

export default class FnrFetch3 {
  constructor(url, postData, options) {
    this.abortController = new AbortController();

    if (url) this.url = url;
    if (postData) this.postData = postData;
    if (options) this.options = options;
  }

  createCaller = (url, verb = "post", data) => {
    if (!url && this.url) url = this.url;
    if (!data && this.postData) data = this.postData;

    return new Promise((fnrFetchResolve, fnrFetchReject) => {
      // signal is spread last so options can never replace it: a caller that
      // passes its own signal would silently detach cancel() from the request,
      // with no error anywhere. withCredentials stays overridable on purpose.
      sendRequest(verb, createUrl(url), data, {
        withCredentials: true,
        ...this.options,
        signal: this.abortController.signal,
      })
        .then((response) => {
          if (response.data.success) return fnrFetchResolve(response.data);
          fnrFetchReject(response.data);
        })
        .catch((error) => {
          // Swallowing cancellations is deliberate and reviewed: a cancelled
          // request's promise stays pending forever, so consumer .then/.catch
          // never fire after cancel(). Rejecting instead would surface error
          // UI on every unmount-cancel and poll-cancel across the app.
          if (!axios.isCancel(error)) fnrFetchReject(error);
        });
    });
  };

  get(url) {
    return this.createCaller(url, "get");
  }

  post(url, postData) {
    return this.createCaller(url, "post", postData);
  }

  patch(url, patchData) {
    return this.createCaller(url, "patch", patchData);
  }

  delete(url, patchData) {
    return this.createCaller(url, "delete", patchData);
  }

  getFormData(data = {}) {
    const formData = new FormData();

    if (typeof data === "object")
      for (let key in data) formData.append(key, data[key]);

    return formData;
  }

  cancel() {
    this.abortController.abort();
  }

  mock(returnData, delay = 0.5) {
    return new Promise((resolve, reject) => {
      setTimeout(
        () =>
          returnData.success
            ? resolve(returnData)
            : reject(returnData.error, returnData),
        delay * 1000
      );
    });
  }
}

function getRandomLoadTime(min = 500, max = 1500) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

const mockPromise = (resp, respOverride, loadTime) => {
  const success = respOverride ? respOverride.success : resp.success;

  return new Promise((resolve, reject) => {
    setTimeout(() => {
      return success
        ? resolve(respOverride || resp)
        : reject(respOverride || resp);
    }, loadTime || getRandomLoadTime());
  });
};

const callMockResponse = (resp, loadTime) => {
  if (resp instanceof Function) resp = resp();
  return {
    get: (respOverride) => mockPromise(resp, respOverride, loadTime),
    post: (respOverride) => mockPromise(resp, respOverride, loadTime),
    patch: (respOverride) => mockPromise(resp, respOverride, loadTime),
    delete: (respOverride) => mockPromise(resp, respOverride, loadTime),
    cancel: () => {},
  };
};

export { callMockResponse };
